import Image from 'next/image'
import { tm } from '@captaincause/ui/tm'
import { flattenImage } from 'utils/strapi/image'
import { CTABanner } from 'components/BlockCTABanner'
import { Button } from 'components/Button'
import { StrapiImage } from 'components/StapiImage'
import type { CTABannerProps } from 'components/BlockCTABanner'
import type { LocalImage, StrapiApiImage } from 'types/strapi/image'
import type { StrapiLink } from 'types/strapi/link'

type UseCase = {
  title: string
  description?: string
  image: StrapiApiImage | LocalImage
}

export interface UseCasesProps {
  banner?: CTABannerProps | null
  color: 'white' | 'purple' | 'beige'
  link?: (StrapiLink & { locale?: false | undefined }) | null
  title: string | null
  useCases: UseCase[] | null
}

const COLORS: Record<UseCasesProps['color'], string> = {
  white: 'bg-white',
  purple: 'bg-medium-purple',
  beige: 'bg-medium-beigee',
}

export default function UseCases({
  banner = null,
  link = null,
  color: propsColor,
  title,
  useCases,
}: UseCasesProps) {
  const color = propsColor ?? 'white'

  return (
    <div
      className={tm(
        'mx-auto h-auto max-w-[1400px] overflow-hidden bg-white px-4 py-12 md:px-8 md:py-14',
        COLORS[color],
      )}
    >
      <div
        className={tm(
          'flex w-full flex-col items-start justify-between md:flex-row',
          !link && 'justify-center',
          !link && 'md:flex-row-reverse',
        )}
      >
        {title ? (
          <h2
            className={tm(
              'order-3 mr-4 mt-4 max-w-[unset] flex-1 text-center font-default text-4xl font-semibold md:order-[initial] md:mt-0 md:max-w-[900px] md:text-left',
              !link && 'mr-0 md:text-center',
            )}
          >
            {title}
          </h2>
        ) : null}
        {link ? (
          <Button
            className={tm('order-2 w-full md:order-[initial] md:w-[unset]')}
            href={link.url}
            locale={link.locale}
            variant="secondary"
            iconRight="arrow-go"
          >
            {link.title}
          </Button>
        ) : null}
      </div>

      {useCases ? (
        <div
          className={tm(
            'mx-auto flex w-full flex-col flex-wrap items-center justify-center pt-10 md:flex-row md:items-baseline md:space-x-4 md:pt-14 lg:space-x-16',
            banner && 'pb-14',
          )}
        >
          {useCases.map((useCase, index) => {
            const image =
              'data' in useCase.image
                ? flattenImage(useCase.image)
                : useCase.image

            return (
              <div
                key={useCase.title}
                className={tm(
                  'max-w-[450px] flex-[33%] py-4 md:max-w-[250px] md:py-0 lg:max-w-[300px] md:[&:nth-child(3n+4)]:ml-0 md:[&:nth-child(3n+4)]:border-none',
                  index === 0 && 'pt-0 md:border-none',
                  index > 2 && 'mt-0 md:mt-8',
                )}
              >
                {image ? (
                  <div className="mb-6 flex justify-center">
                    {'url' in image ? (
                      <StrapiImage {...image} className="h-[80px] w-auto" />
                    ) : (
                      <Image
                        src={image.image}
                        height={80}
                        alt={image.alt}
                        className="h-[80px] w-auto"
                      />
                    )}
                  </div>
                ) : null}

                {useCase.title ? (
                  <div className="text-center text-2xl font-semibold">
                    {useCase.title}
                  </div>
                ) : null}
                {useCase.description ? (
                  <div className="mt-2 text-center text-lg text-dark-gray">
                    {useCase.description}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
      {banner ? <CTABanner {...banner} /> : null}
    </div>
  )
}
